import { Price } from '@jouzen/ecom-components/v3/Price';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import type { MessageKey } from 'types/LocaleMessageKey';

import RichText from '@/app/components/RichText';
import type { SupportedCurrency } from '@/app/types/currency';

export interface PriceInfoProps {
  readonly className?: string;
  readonly priceClassName?: string;
  readonly dataCy?: string;
  readonly currencyCode: SupportedCurrency;
  readonly discountedPrice?: number | null | undefined;
  readonly basePrice?: number | null | undefined;
  readonly priceText?: MessageKey | null | undefined;
  readonly showDecimalsForWholeDollars?: boolean | undefined;
}

const PriceLoader = () => (
  <span
    className="block h-lh w-[80px] animate-pulse rounded-md bg-gray-300"
    data-cy="price-loader"
  >
    <span className="sr-only">
      <RichText messageKey="loading" />
    </span>
  </span>
);

const priceTValues = ({
  basePrice,
  discountedPrice,
  currencyCode,
  showDecimalsForWholeDollars = false,
}: Pick<
  PriceInfoProps,
  'discountedPrice' | 'currencyCode' | 'showDecimalsForWholeDollars'
> & { basePrice: number }) => ({
  price: () => (
    <Price
      showDecimalsForWholeDollars={showDecimalsForWholeDollars}
      price={basePrice}
      currencyCode={currencyCode}
      {...(discountedPrice != null && {
        discountedPrice: {
          amount: discountedPrice,
          currencyCode,
        },
      })}
    />
  ),
});

const PriceInfo = ({
  className,
  priceClassName,
  dataCy,
  currencyCode,
  discountedPrice,
  basePrice,
  priceText,
  showDecimalsForWholeDollars = false,
}: PriceInfoProps) => {
  return (
    <span className={className} data-cy={dataCy}>
      {basePrice == null ? (
        <PriceLoader />
      ) : (
        <Typography className={cx('whitespace-pre-wrap', priceClassName)}>
          {priceText ? (
            <RichText
              messageKey={priceText}
              tValues={priceTValues({
                basePrice,
                discountedPrice,
                currencyCode,
                showDecimalsForWholeDollars,
              })}
            />
          ) : (
            <Price
              showDecimalsForWholeDollars={showDecimalsForWholeDollars}
              price={basePrice}
              currencyCode={currencyCode}
              {...(discountedPrice != null && {
                discountedPrice: { amount: discountedPrice, currencyCode },
              })}
            />
          )}
        </Typography>
      )}
    </span>
  );
};

export default PriceInfo;
